import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageSEO, AboutPageSchema, BreadcrumbSchema } from "../components/SEO";

const stats = [
  { label: "Projects", value: "6+" },
  { label: "Genres", value: "5" },
  { label: "In Production", value: "2" },
  { label: "Platforms", value: "3" },
];

export default function AboutPage() {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#050505]">
      <PageSEO
        title="About"
        description="Learn about Mist Media, a future-forward production company based in Los Angeles dedicated to creating bold, compelling narratives across film, television, and digital platforms. Founded in 2022."
        path="/about"
        schema={[AboutPageSchema(), BreadcrumbSchema(breadcrumbs)]}
      />

      {/* Hero */}
      <section className="relative h-[55vh] md:h-[65vh] overflow-hidden flex items-end" aria-label="About hero">
        <div className="absolute inset-0">
          <img
            src="/api/uploads/about-hero-new.jpg"
            alt="Mist Media production set - behind the scenes of filmmaking"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#050505]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-16">
          <div className="max-w-[1400px] mx-auto">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
                <li><Link to="/" className="text-[#52525B] hover:text-white transition-colors">Home</Link></li>
                <li><ChevronRight size={10} className="text-[#3f3f46]" /></li>
                <li className="text-[#A1A1AA]">About</li>
              </ol>
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">About Us</p>
              </div>
              <h1 className="font-['Syne'] text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-[-0.04em] text-white">
                The Vision
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" aria-label="Our story">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6"
            >
              <h2 className="font-['Syne'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-10">
                We believe in the power of stories to shape the future.
              </h2>
              <div className="w-16 h-px bg-[#38BDF8] mb-10" aria-hidden="true" />
              <p className="text-sm text-[#A1A1AA] leading-[1.8] mb-6">
                Mist Media is a future-forward production company dedicated to creating bold, compelling narratives across film, television, and digital platforms. Founded with the belief that storytelling is the most powerful tool we have to connect, challenge, and inspire.
              </p>
              <p className="text-sm text-[#71717A] leading-[1.8]">
                Our projects span genres from psychological thrillers to dark comedies, each united by a commitment to authentic voices, distinctive vision, and production excellence. We work with emerging and established talent to bring stories to life that audiences cannot look away from.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-6 space-y-14"
            >
              {[
                {
                  title: "Our Mission",
                  text: "To produce content that pushes boundaries and resonates globally. We seek out stories that live at the intersection of entertainment and meaning -- narratives that stay with you long after the credits roll."
                },
                {
                  title: "Our Approach",
                  text: "We embrace technology, new distribution models, and cross-platform storytelling. From traditional film to immersive digital experiences, we meet audiences wherever they are -- always with quality and intention at the forefront."
                },
                {
                  title: "What Drives Us",
                  text: "A relentless belief that the best stories have not been told yet. Every project we take on is a chance to create something that matters -- something audiences will remember."
                },
              ].map((item, i) => (
                <div key={item.title}>
                  <h3 className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8] mb-4">{item.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-[1.8]">{item.text}</p>
                  {i < 2 && <div className="mt-14 h-px bg-[#141414]" />}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#141414]" aria-label="Company statistics">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <p className="font-['Syne'] text-4xl md:text-5xl font-extrabold text-white tracking-tight">{stat.value}</p>
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#3f3f46] mt-3">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#141414]" aria-label="Get in touch">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white tracking-tight mb-8">
              Interested in working with us?
            </h2>
            <Link
              to="/contact"
              className="btn-press group inline-flex items-center gap-3 px-10 py-4 bg-white text-[#050505] text-xs font-bold tracking-[0.2em] uppercase rounded-none hover:bg-[#38BDF8] transition-all duration-300"
            >
              Get in Touch
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
